import type { BobRunReport } from "../../api/bobReports";
import { byFolder, fileLine, fmtDateTime, partialSeedReason, repoName, runState, sevCounts, shortSha, usd, worstFirst } from "../../lib/bobReports";
import { headlineOf } from "../../lib/ledgerFormat";
import type { ReportNav } from "./reportNav";
import { ReportCrumbs, SameHeadNote } from "./RunReportView";
import { CatChip, SevChip, SevStack, Tile } from "./reportParts";

const WORST_SHOWN = 6;

const STATE_CHIP = {
  completed: { label: "Complete", cls: "br-chip-ok" },
  partial: { label: "Partial", cls: "br-chip-flag" },
  failed: { label: "Did not finish", cls: "br-chip-block" },
  displaced: { label: "Complete", cls: "br-chip-ok" },
} as const;

export default function SeedReportView({ report, nav }: { report: BobRunReport; nav: ReportNav }) {
  const { run, findings } = report;
  const state = runState(run);
  const chip = STATE_CHIP[state];
  const partial = partialSeedReason(run);
  const mix = sevCounts(findings.new);
  const complete = findings.new.length === findings.new_total;
  const worst = worstFirst(findings.new).slice(0, WORST_SHOWN);
  const areas = byFolder(findings.new);
  const maxArea = Math.max(...areas.map((a) => a.total), 1);
  const known = typeof run.reconfirmed === "number" ? run.reconfirmed : null;
  const cost = nav.isAdmin && typeof run.cost_usd === "number" ? run.cost_usd : null;

  return (
    <div className="br-page br-narrow">
      {partial !== null && (
        <section className="br-banner" role="alert" aria-label="Partial review">
          <div className="br-banner-head">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3 22 20H2z" /><path d="M12 10v4M12 17h.01" />
            </svg>
            <strong style={{ fontSize: 15 }}>Partial review: Bob stopped before the whole pack was read</strong>
          </div>
          <p>{partial}</p>
          <p className="br-soft">Everything found so far is saved to the ledger. The next whole-pack review covers the pack again.</p>
        </section>
      )}

      <header className="br-head-col">
        <ReportCrumbs nav={nav} here={`Whole-pack review ${run.id}`} />
        <div className="br-row">
          <h2 className="br-h1">Whole-pack review</h2>
          <CatChip size="lg">{repoName(run.repo)} / {run.branch} at {shortSha(run.head_sha)}</CatChip>
          <span className={`br-chip br-lg ${chip.cls}`}>{chip.label}</span>
        </div>
        <span className="br-meta">{fmtDateTime(run.created_at)}</span>
        <p className="br-intro">
          Bob read the pack's tracked files, not a diff. <strong>{findings.new_total} new {findings.new_total === 1 ? "problem" : "problems"}</strong>
          {complete && (mix.BLOCK ?? 0) > 0 ? `, ${mix.BLOCK} of them release ${mix.BLOCK === 1 ? "blocker" : "blockers"}` : ""}.
        </p>
        <SameHeadNote report={report} nav={nav} />
      </header>

      {state === "failed" && run.error && (
        <div className="br-banner" role="alert">
          <div className="br-banner-head"><strong>This review did not finish</strong></div>
          <p>{run.error}</p>
        </div>
      )}

      <section aria-label="Totals" className="br-grid-tiles">
        <Tile label="Findings" value={findings.new_total}
          note={complete ? `${mix.BLOCK ?? 0} BLOCK, ${mix.FLAG ?? 0} FLAG, ${mix.SUGGEST ?? 0} SUGGEST` : `the worst ${findings.new.length} listed below`}>
          {complete && <SevStack counts={mix} total={findings.new_total} />}
        </Tile>
        {known !== null && (
          <Tile label="New vs known" value={<>{findings.new_total} <small>new</small></>}
            note={`${known} open ${known === 1 ? "finding" : "findings"} Bob saw again`}>
            <div className="br-stack" aria-hidden="true">
              {findings.new_total > 0 && <span className="br-bg-accent" style={{ width: `${(100 * findings.new_total) / (findings.new_total + known)}%` }} />}
              {known > 0 && <span style={{ width: `${(100 * known) / (findings.new_total + known)}%`, background: "var(--line)" }} />}
            </div>
          </Tile>
        )}
        {cost !== null && <Tile label="Cost" value={usd(cost)} />}
      </section>

      {worst.length > 0 && (
        <section className="br-panel" aria-label="Worst player-facing problems">
          <div className="br-row" style={{ justifyContent: "space-between", gap: 8 }}>
            <h2 className="br-h2">Worst for players, first</h2>
            <span className="br-small br-muted">Sorted by severity, then by whether Bob named what a player sees</span>
          </div>
          <ol className="br-worst">
            {worst.map((f, i) => (
              <li key={f.id}>
                <a href={nav.findingHref(f.id)} data-finding={f.id} onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  nav.openFinding(f.id);
                }}>
                  <span className="br-num br-muted" style={{ fontSize: 13, paddingTop: 2 }}>{i + 1}</span>
                  <span className="br-worst-body">
                    <span className="br-item-head">
                      <SevChip severity={f.severity} />
                      {f.category && <CatChip>{f.category}</CatChip>}
                      <span className="br-spacer" />
                      <span className="br-mono br-tiny br-muted">#{f.id}</span>
                    </span>
                    <span className="br-item-title">{headlineOf(f)}</span>
                    <span className="br-mono br-tiny br-soft" style={{ overflowWrap: "anywhere" }}>{fileLine(f)}</span>
                  </span>
                </a>
              </li>
            ))}
          </ol>
          <button type="button" className="br-linkbtn" style={{ alignSelf: "flex-start", fontSize: 13.5 }} onClick={() => nav.openTab("ledger")}>
            See all {findings.new_total} in the console
          </button>
        </section>
      )}

      {areas.length > 0 && (
        <section className="br-panel" aria-label="Findings by area">
          <div className="br-row" style={{ justifyContent: "space-between", gap: 8 }}>
            <h2 className="br-h2">Findings by area</h2>
            <span className="br-legend">
              <span><i className="br-bg-block" />BLOCK</span>
              <span><i className="br-bg-flag" />FLAG</span>
              <span><i className="br-bg-suggest" />SUGGEST</span>
            </span>
          </div>
          {!complete && <span className="br-note">Counted over the {findings.new.length} findings listed, not all {findings.new_total}.</span>}
          <div className="br-table-wrap">
            <table className="br-table" style={{ minWidth: 560 }}>
              <thead>
                <tr>
                  <th scope="col">Area</th>
                  <th scope="col" style={{ width: "40%" }}>Severity mix</th>
                  <th scope="col" className="br-r">BLOCK</th>
                  <th scope="col" className="br-r">Files</th>
                  <th scope="col" className="br-r">Total</th>
                </tr>
              </thead>
              <tbody>
                {areas.map((a) => (
                  <tr key={a.folder}>
                    <td><span className="br-mono" style={{ fontSize: 12.5 }}>{a.folder}</span></td>
                    <td>
                      <div className="br-mix" style={{ width: `${((100 * a.total) / maxArea).toFixed(1)}%` }} aria-hidden="true">
                        {(["BLOCK", "FLAG", "SUGGEST"] as const).map((s) => a[s] > 0 && (
                          <span key={s} className={`br-bg-${s.toLowerCase()}`} style={{ flex: `${a[s]} 1 0`, minWidth: 3 }} />
                        ))}
                      </div>
                    </td>
                    <td className="br-r br-num" style={{ color: a.BLOCK > 0 ? "var(--block)" : "var(--ink-3)" }}>{a.BLOCK}</td>
                    <td className="br-r br-num br-soft">{a.files}</td>
                    <td className="br-r br-num" style={{ fontWeight: 600 }}>{a.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {findings.new_total === 0 && state !== "failed" && (
        <section className="br-panel"><p className="br-p">Bob found nothing new in this pass.</p></section>
      )}
    </div>
  );
}
